import { DateValue } from "@mantine/dates"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { FilterChipData } from "../odoo/filter-chip"

export type DropShippingFilters = {
  salesCode?: string
  createdAt?: [DateValue, DateValue]
  orderType?: string | null
  orderStatus?: string | null
  orderedUserFirstName?: string
  receiverBusinessPartnerRefCodeBusinessName?: string
  receiverBusinessRegNumber?: string
  receiverBusinessRefCodeProfileName?: string
  receiverBranchName?: string
  receiverStaffFirstNamePhone?: string
  regionName?: string
  directionName?: string
  deliveryDate?: [DateValue, DateValue]
  deliveryNoteStatus?: string | null
  deliveryNoteStaffFirstNameEmail?: string
  deliveryNoteDeliveredDate?: [DateValue, DateValue]
  totalAmount: [string | number | null, string | number | null]
  paidAmount: [string | number | null, string | number | null]
  amountToPay: [string | number | null, string | number | null]
  paymentStatus?: string | null
  paymentTermDescription?: string
  purchaseCode?: string
  respondedDate?: [DateValue, DateValue]
  purchaseType?: string | null
  orderedUserFirstNamePhone?: string
  searchQuery?: string
}

const initialFilter: DropShippingFilters = {
  salesCode: "",
  createdAt: [null, null],
  orderType: null,
  orderStatus: null,
  orderedUserFirstName: "",
  receiverBusinessPartnerRefCodeBusinessName: "",
  receiverBusinessRegNumber: "",
  receiverBusinessRefCodeProfileName: "",
  receiverBranchName: "",
  receiverStaffFirstNamePhone: "",
  deliveryDate: [null, null],
  deliveryNoteStatus: null,
  deliveryNoteStaffFirstNameEmail: "",
  deliveryNoteDeliveredDate: [null, null],
  totalAmount: [null, null],
  paidAmount: [null, null],
  amountToPay: [null, null],
  paymentStatus: null,
  paymentTermDescription: "",
  purchaseCode: "",
  respondedDate: [null, null],
  purchaseType: null,
  orderedUserFirstNamePhone: "",
  searchQuery: "",
}

type FilterLabelMap = {
  key: keyof DropShippingFilters
  label: string
  format?: (val: any) => string | null
}

function getDisplayValue(val: any): string | null {
  if (val === null || val === undefined || val === "") return null
  if (Array.isArray(val)) {
    if (val.every((v) => v === null || v === undefined || v === "")) return null
    return val.filter(Boolean).join(" - ")
  }
  return String(val)
}

export function useDropShippingFilters() {
  const { t } = useTranslation(["supplierSales", "common"])
  const [filters, setFilters] = useState<DropShippingFilters>(structuredClone(initialFilter))

  const FILTER_LABELS: FilterLabelMap[] = [
    { key: "salesCode", label: t("supplierSales:dropshipping.filters.soNumber") },
    { key: "purchaseCode", label: t("supplierSales:dropshipping.filters.poNumber") },
    { key: "orderType", label: t("supplierSales:dropshipping.filters.type") },
    { key: "orderStatus", label: t("supplierSales:dropshipping.filters.status") },
    { key: "purchaseType", label: t("supplierSales:dropshipping.filters.poType") },
    { key: "paymentStatus", label: t("supplierSales:dropshipping.filters.paymentStatus") },
    { key: "deliveryNoteStatus", label: t("supplierSales:dropshipping.filters.deliveryStatus") },
    { key: "receiverBusinessPartnerRefCodeBusinessName", label: t("supplierSales:dropshipping.filters.partnerName") },
    { key: "receiverBranchName", label: t("supplierSales:dropshipping.filters.receiverBranch") },
    { key: "searchQuery", label: t("supplierSales:dropshipping.filters.search") },
    {
      key: "createdAt",
      label: t("supplierSales:dropshipping.filters.date"),
      format: (val: [DateValue, DateValue]) => {
        if (!val || (!val[0] && !val[1])) return null
        return `${val[0] || "..."} - ${val[1] || "..."}`
      },
    },
    {
      key: "deliveryDate",
      label: t("supplierSales:dropshipping.filters.deliveryDate"),
      format: (val: [DateValue, DateValue]) => {
        if (!val || (!val[0] && !val[1])) return null
        return `${val[0] || "..."} - ${val[1] || "..."}`
      },
    },
  ]

  const setFilter = <K extends keyof DropShippingFilters>(key: K, value: DropShippingFilters[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
  }

  const clearFilters = () => {
    setFilters(structuredClone(initialFilter))
  }

  const removeFilter = (key: keyof DropShippingFilters) => {
    setFilters((prev) => ({ ...prev, [key]: (initialFilter as any)[key] }))
  }

  const activeFilterChips: FilterChipData[] = FILTER_LABELS.reduce<FilterChipData[]>((chips, item) => {
    const val = filters[item.key]
    const display = item.format ? item.format(val) : getDisplayValue(val)
    if (display) {
      chips.push({ key: item.key, label: item.label, value: display, onRemove: () => removeFilter(item.key) })
    }
    return chips
  }, [])

  const { searchQuery, ...apiFilters } = filters

  return { filters: apiFilters, setFilters, setFilter, clearFilters, removeFilter, activeFilterChips, searchQuery }
}
